import { Theme, SxProps, alpha } from '@mui/material';
import React from 'react';

import { Box, BoxProps } from '_lib/components/Box/Box';
import { Card, CardProps } from '_lib/components/Card/Card';
import { Divider, DividerProps } from '_lib/components/Divider/Divider';
import { IconButton, IconButtonProps } from '_lib/components/IconButton/IconButton';
import { Tab, TabProps } from '_lib/components/Tab/Tab';
import { Tabs, TabsProps } from '_lib/components/Tabs/Tabs';
import { toSxArray } from '_lib/utils/helpers';

export const VerticalDivider = (props: DividerProps) => {
  const { sx, ...otherProps } = props;
  return (
    <Divider
      orientation='vertical'
      flexItem
      sx={[
        (theme) => ({ borderColor: alpha(theme.palette.common.black, 0.13), marginRight: '-1px', zIndex: 1 }),
        ...toSxArray(sx)
      ]}
      {...otherProps}
    />
  );
};

export const StyledDivider = (props: DividerProps) => {
  const { sx, ...otherProps } = props;
  return (
    <Divider
      sx={[(theme) => ({ borderColor: alpha(theme.palette.common.white, 0.18) }), ...toSxArray(sx)]}
      {...otherProps}
    />
  );
};

export const StyledTab = (props: TabProps & { forceEnableRipple?: boolean }) => {
  const { forceEnableRipple, sx, ...otherProps } = props;
  return (
    <Tab
      disableRipple={!forceEnableRipple}
      sx={[
        (theme) => ({
          color: theme.palette.primary.contrastText,
          '&.MuiTab-root.Mui-selected, &:hover': {
            color: theme.palette.secondary.main
          }
        }),
        ...toSxArray(sx)
      ]}
      {...otherProps}
    />
  );
};

export const StyledTabs = (props: TabsProps) => {
  const { sx, ...otherProps } = props;
  return (
    <Tabs
      sx={[
        (theme) => ({
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.secondary.main
          }
        }),
        ...toSxArray(sx)
      ]}
      {...otherProps}
    />
  );
};

export const SimpleContentBox = (props: BoxProps) => {
  const { sx, ...otherProps } = props;
  return (
    <Box sx={[(theme) => ({ backgroundColor: theme.palette.background.default }), ...toSxArray(sx)]} {...otherProps} />
  );
};

export const ContentBox = (props: BoxProps) => {
  const saneDefaults = {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    position: 'absolute',
    height: '100%',
    width: '100%',
    ...props
  } as const;
  return <SimpleContentBox {...saneDefaults} />;
};

const getPrimaryHeader = (theme: Theme) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  textTransform: 'uppercase',
  textOverflow: 'ellipsis'
});

export const PrimaryHeaderIconButton = (props: IconButtonProps) => {
  const { sx, ...otherProps } = props;
  return <IconButton {...otherProps} sx={[(theme) => getPrimaryHeader(theme), ...toSxArray(sx)]} />;
};

export const PrimaryHeader = (props: BoxProps) => {
  const { sx, ...otherProps } = props;
  return <Box sx={[(theme) => getPrimaryHeader(theme), ...toSxArray(sx)]} {...otherProps} />;
};

/** @deprecated - use library component */
export const FlexCard = (props: Omit<CardProps, 'display'>) => {
  return <Card {...props} sx={[{ width: '100%', height: '100%' }, ...toSxArray(props.sx), { display: 'flex' }]} />;
};

// TODO - turn this into a button link?
export const ContrastIconButton = (props: IconButtonProps) => {
  return (
    <IconButton {...props} sx={[...toSxArray(props.sx), (theme) => ({ color: theme.palette.callPane.contrastText })]} />
  );
};

export const roundedAccordion: SxProps<Theme> = {
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4
};
